import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Instagram, ArrowUpRight } from "lucide-react";

/*
  Instagram strip — 6 curated posts above the newsletter.
  - Data source: GET /api/instagram-posts (admin can replace via POST /api/admin/instagram-posts)
  - If admin hasn't seeded posts yet, backend returns the 6 newest product images as a
    tasteful fallback with every tile linking to the real Adiyogi Instagram profile.
*/

export default function InstagramStrip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/instagram-posts", { params: { limit: 6 } }).then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data || !data.posts?.length) return null;
  const posts = data.posts.slice(0, 6);
  const profileUrl = data.profile_url;
  const handle = data.handle || "the_adiyogi_spiritual";

  return (
    <section className="bg-white border-t hairline" data-testid="instagram-strip">
      <div className="container-x py-14 md:py-20">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] gold-text mb-2">
              <Instagram className="w-3.5 h-3.5" strokeWidth={1.5} /> Follow us on Instagram
            </div>
            <h2 className="font-serif text-3xl md:text-4xl">@{handle}</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Latest from our collection — sacred décor, festive moments and behind-the-scenes craft.
            </p>
          </div>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-gold rounded-full px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
            data-testid="ig-follow-cta"
          >
            Follow on Instagram <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </a>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
          {posts.map((p, i) => (
            <a
              key={i}
              href={p.permalink || profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="relative group overflow-hidden aspect-square bg-muted rounded-md border hairline"
              data-testid={`ig-tile-${i}`}
              aria-label={p.caption || `Instagram post ${i + 1}`}
            >
              <img src={p.image_url} alt={p.caption || `@${handle} on Instagram`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/60 transition flex items-center justify-center">
                <Instagram className="w-6 h-6 text-ivory" strokeWidth={1.5} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
